<div class="signup-page" authTheme>
  <div class="signup-container">

    <div class="text-center mb-4">
      <img [src]="logo" alt="ADN Logo" class="signup-logo mb-3" />
    </div>

    <div *ngIf="currentView === 'role-pick'" class="role-pick">
      <h2 class="h3 text-center mb-2">Create Account</h2>
      <p class="text-muted mb-4 text-center">Tell us how you'll use American Drone Network.</p>

      <div *ngIf="invitationMessage" class="alert alert-info" role="alert">
        <i class="bi bi-envelope-fill me-2"></i>
        {{ invitationMessage }}
      </div>

      <button type="button" class="role-tile mb-3 w-100 text-start" (click)="selectPilot()">
        <div class="d-flex align-items-start">
          <div class="role-icon me-3 d-flex align-items-center justify-content-center flex-shrink-0">
            <i class="bi bi-airplane"></i>
          </div>
          <div class="flex-grow-1">
            <h5 class="mb-1">Drone Applicator</h5>
            <p class="text-muted small mb-1">I fly spray jobs</p>
            <p class="small mb-0">
              For licensed commercial pilots running agricultural drone spray operations.
              Track your spray records, certifications, drones, and customers — and find
              new spray work nearby.
            </p>
          </div>
        </div>
      </button>

      <button type="button" class="role-tile mb-3 w-100 text-start" (click)="selectFarmer()">
        <div class="d-flex align-items-start">
          <div class="role-icon me-3 d-flex align-items-center justify-content-center flex-shrink-0">
            <i class="bi bi-flower3"></i>
          </div>
          <div class="flex-grow-1">
            <h5 class="mb-1">Farmer</h5>
            <p class="text-muted small mb-1">I need spray jobs</p>
            <p class="small mb-0">
              For growers who need spray jobs done on their fields. Post spray jobs,
              review pilot bids, and manage your operation.
            </p>
          </div>
        </div>
      </button>

      <div class="text-center mt-4">
        <p class="text-muted mb-0">
          Already have an account?
          <a routerLink="/login" class="text-decoration-none fw-medium">Sign In</a>
        </p>
      </div>
    </div>

    <div *ngIf="currentView === 'farmer-block'" class="py-3 text-center">
      <div class="mb-3">
        <i class="bi bi-phone display-4 text-primary"></i>
      </div>
      <h2 class="h3 mb-3">Farmer signup is in the mobile app</h2>
      <p class="text-muted mb-4">
        The American Drone Network app is our mobile companion for farmers.
        Download it on your iPhone or iPad to create your farmer account, post
        spray jobs, and review pilot bids.
      </p>

      <div class="mb-4">
        <a
          href="https://apps.apple.com/us/app/american-drone-network/id6773354275"
          target="_blank"
          rel="noopener noreferrer"
          class="btn btn-dark btn-lg d-inline-flex align-items-center"
        >
          <i class="bi bi-apple me-2 fs-4"></i>
          <span class="text-start lh-1">
            <small class="d-block store-badge-caption">Download on the</small>
            <span class="fw-semibold">App Store</span>
          </span>
        </a>
      </div>

      <button type="button" class="btn btn-link" (click)="backToRolePick()">
        ← Back
      </button>
    </div>

    <div *ngIf="currentView === 'form'" class="signup-form">
      <h2 class="h3 text-center mb-2">Create Your Account</h2>
      <p class="text-center text-muted mb-4">Join American Drone Network today</p>

      <div *ngIf="invitationMessage" class="alert alert-info" role="alert">
        <i class="bi bi-envelope-fill me-2"></i>
        {{ invitationMessage }}
      </div>

      <div *ngIf="errorMessage" class="alert alert-danger" role="alert">
        <i class="bi bi-exclamation-triangle me-2"></i>
        {{ errorMessage }}
      </div>

      <form [formGroup]="signupForm" (ngSubmit)="onSignup()">

        <h5 class="mt-2 mb-3">Personal Information</h5>

        <div class="row">
          <div class="col-md-6">
            <app-form-input
              label="First Name"
              type="text"
              placeholder="John"
              formControlName="firstName"
              [required]="true"
              [errorMessage]="getErrorMessage('firstName')"
            ></app-form-input>
          </div>
          <div class="col-md-6">
            <app-form-input
              label="Last Name"
              type="text"
              placeholder="Doe"
              formControlName="lastName"
              [required]="true"
              [errorMessage]="getErrorMessage('lastName')"
            ></app-form-input>
          </div>
        </div>

        <app-form-input
          label="Email Address"
          type="email"
          placeholder="you@example.com"
          formControlName="email"
          mask="email"
          [required]="true"
          [errorMessage]="getErrorMessage('email')"
        ></app-form-input>

        <app-form-input
          *ngIf="!invitationId"
          label="Phone"
          type="tel"
          placeholder="(555) 123-4567"
          formControlName="phone"
          mask="phone"
          [required]="true"
          [errorMessage]="getErrorMessage('phone')"
        ></app-form-input>

        <app-form-password
          #password
          label="Password"
          placeholder="Create a strong password"
          formControlName="password"
          [required]="true"
          [showRequirements]="true"
          [errorMessage]="getErrorMessage('password')"
        ></app-form-password>

        <app-form-password
          label="Confirm Password"
          placeholder="Re-enter your password"
          formControlName="confirmPassword"
          [required]="true"
          [errorMessage]="getErrorMessage('confirmPassword')"
        ></app-form-password>

        <ng-container *ngIf="!invitationId">
          <h5 class="mt-4 mb-3">Business Information</h5>

          <app-form-input
            label="Business Name"
            type="text"
            placeholder="e.g. Smith Aerial Services"
            formControlName="organizationName"
            [required]="true"
            [errorMessage]="getErrorMessage('organizationName')"
          ></app-form-input>

          <app-form-input
            label="Street Address"
            type="text"
            placeholder="123 Main St"
            formControlName="address"
            [required]="true"
            [errorMessage]="getErrorMessage('address')"
          ></app-form-input>

          <div class="row">
            <div class="col-md-6">
              <app-form-input
                label="City"
                type="text"
                placeholder="Springfield"
                formControlName="city"
                [required]="true"
                [errorMessage]="getErrorMessage('city')"
              ></app-form-input>
            </div>
            <div class="col-md-3">
              <!-- Native select — the shared form-input is input-only.
                   Stores the 2-letter code; the dropdown shows full names. -->
              <div class="form-group mb-3">
                <label class="form-label">
                  State <span class="text-danger">*</span>
                </label>
                <select
                  class="form-select"
                  [class.is-invalid]="hasError('state')"
                  formControlName="state"
                >
                  <option value="">Select…</option>
                  <option *ngFor="let s of states" [value]="s.code">
                    {{ s.name }} ({{ s.code }})
                  </option>
                </select>
                <div *ngIf="getErrorMessage('state')" class="invalid-feedback d-block">
                  {{ getErrorMessage('state') }}
                </div>
              </div>
            </div>
            <div class="col-md-3">
              <app-form-input
                label="ZIP Code"
                type="text"
                placeholder="62704"
                formControlName="zip"
                mask="zip"
                [required]="true"
                helperText="5 digits"
                [errorMessage]="getErrorMessage('zip')"
              ></app-form-input>
            </div>
          </div>

          <!-- Terms of Service agreement (pilot path only). -->
          <div class="form-check mt-2 mb-3">
            <input
              type="checkbox"
              class="form-check-input"
              id="agreedToTerms"
              [class.is-invalid]="hasError('agreedToTerms')"
              formControlName="agreedToTerms"
            />
            <label class="form-check-label" for="agreedToTerms">
              I agree to the
              <a [href]="legalUrls.termsOfUse" target="_blank" rel="noopener noreferrer" class="text-decoration-none">Terms of Service</a>
              and
              <a [href]="legalUrls.privacyPolicy" target="_blank" rel="noopener noreferrer" class="text-decoration-none">Privacy Policy</a>.
            </label>
            <div *ngIf="hasError('agreedToTerms')" class="invalid-feedback d-block">
              You must agree to the Terms of Service and Privacy Policy.
            </div>
          </div>
        </ng-container>

        <!-- Submit -->
        <app-form-button
          label="Create Account"
          variant="primary"
          size="lg"
          type="submit"
          [loading]="loading"
          class="w-100 mt-3"
        ></app-form-button>
      </form>

      <!-- Footer links: back to role picker (pilot path) + sign in. -->
      <div class="text-center mt-4">
        <button
          *ngIf="!invitationId"
          type="button"
          class="btn btn-link text-decoration-none"
          (click)="backToRolePick()"
        >
          ← Back
        </button>
        <p class="text-muted mt-2 mb-0">
          Already have an account?
          <a routerLink="/login" class="text-decoration-none fw-medium">Sign In</a>
        </p>
      </div>
    </div>

    <!-- View: Welcome + email verification (post-signup success). -->
    <div *ngIf="currentView === 'welcome'" class="welcome-screen text-center">
      <div class="welcome-content mb-5">
        <div class="success-icon mb-3">
          <i class="bi bi-check-circle-fill text-success"></i>
        </div>
        <h2 class="h3 mb-3">Welcome to American Drone Network!</h2>
        <p class="text-muted mb-4">
          Your account has been created successfully. We're excited to have you join our community.
        </p>
      </div>

      <div class="verification-content mb-5">
        <div class="verification-icon mb-3">
          <i class="bi bi-envelope-check text-primary"></i>
        </div>
        <h4 class="mb-3">Verify your email</h4>
        <p class="text-muted mb-2">
          We've sent a six-digit code and a verification link to
          <strong>{{ userEmail }}</strong>. You'll need to verify before you
          can use your account.
        </p>
        <p class="text-muted small mb-4">
          <i class="bi bi-info-circle me-1"></i>
          Don't see it? Check your spam or junk folder.
        </p>

        <div *ngIf="errorMessage" class="alert alert-danger mb-3" role="alert">
          <i class="bi bi-exclamation-triangle me-2"></i>
          {{ errorMessage }}
        </div>

        <button
          class="btn btn-link"
          (click)="resendVerificationEmail()"
          [disabled]="loading">
          <span *ngIf="!loading">Resend verification email</span>
          <span *ngIf="loading">
            <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            Sending...
          </span>
        </button>
      </div>

      <div class="mt-4">
        <app-form-button
          label="Enter verification code"
          variant="primary"
          size="lg"
          (buttonClick)="continueToVerification()"
          class="w-100"
        ></app-form-button>
      </div>

      <div class="alert alert-info mt-4">
        <i class="bi bi-info-circle me-2"></i>
        Once verified, you can complete your profile and add certifications from your dashboard.
      </div>
    </div>

  </div>
</div>
